/**
 * Show the partner's cam status.
 *
 * @module
 *
 * @import {Amateur} from "../../amateurPool/model/amateur";
 */

import {AttributeLink, AttributeSetup} from '@acng/frontend-stargazer';
import {ARRAY, ENUM, guard} from '@acng/frontend-rubicon';
import {WatchGroup} from '@acng/frontend-relativity';
import {attributeSelector} from '@acng/frontend-bounty/dom/query.js';
import {join, reduce} from '@acng/frontend-bounty/std/array.js';
import {matchAllMap} from '@acng/frontend-bounty/std/string.js';

import {createConditional as Conditional} from 'nova';
import {inject} from '../../core/service/ng.js';
import {ctxAmateur} from '../../amateurPool/context/amateur.js';
import {ctxCamEvent} from '../context/cam-event.js';
import {possibleFlagList} from '../model/livecam.js';

/**
 * Remove the element from the document unless one of the given {@link possibleFlagList | cam flags} apply to
 * the {@link ctxAmateur | partner}.
 *
 * ## Flags
 *
 * ## Formal Syntax
 *
 *     if-cam = <cam-flags>
 *     <cam-flags> = is_online || is_occupied || toycontrol
 *
 * @example
 *
 * ```html
 * <span if-cam="is_online, is_occupied"> #{ icon: streaming } </span>
 * <span if-cam="toycontrol"> #{ icon: toycontrol } </span>
 * ```
 *
 * @group DOM Attribute
 */
const IF_CAM = 'if-cam';

AttributeSetup(IF_CAM, (name) => {
  const syntax = new RegExp(`(${join(possibleFlagList, '|')})`, 'g');
  const Livecam = inject('Livecam');
  const watch = WatchGroup(attributeSelector(name), ctxAmateur, ctxCamEvent);

  /**
   * @param {Amateur | null} partner
   * @param {possibleFlagList[number][]} checkFlags
   *
   * @returns {boolean}
   */
  const check = (partner, checkFlags) => {
    if (!partner) {
      return false;
    }

    const cam = Livecam.get(partner.id);

    if (!cam) {
      return false;
    }

    return reduce(checkFlags, (ok, flag) => ok || cam[flag], false);
  };

  AttributeLink(name, (element, value) => {
    const toggle = Conditional(element);
    const flags = matchAllMap(value, syntax, ([, flag]) => flag);
    ASSERT: guard(flags, ARRAY(ENUM(possibleFlagList)), `[${name}] flags`);

    return (element) => watch(element, (element, [partner]) => toggle(check(partner, flags)));
  });
});

export {IF_CAM};
